<template>
    <main-content>
        <kt-panel>
            <kt-panel-header>
                <kt-panel-header-label>
                    <kt-panel-header-title>
                        {{ $t('settlement.psp_cut_off.title') }}
                        <span class="info-tip" tabindex="0">
                            <i class="la la-exclamation-circle"></i>
                            <span class="info-tip-text">{{ $t('settlement.psp_cut_off.description') }}</span>
                        </span>
                    </kt-panel-header-title>
                </kt-panel-header-label>
            </kt-panel-header>
            <kt-panel-body>
                <div v-if="loading" class="text-center py-5">
                    <div class="kt-spinner kt-spinner--v2 kt-spinner--lg kt-spinner--brand d-inline-block"></div>
                </div>

                <form v-else class="kt-form" @submit.prevent="save">
                    <div class="table-responsive">
                        <table class="table table-bordered table-hover psp-cut-off-table">
                            <thead class="thead-light">
                            <tr>
                                <th>{{ $t('settlement.psp_cut_off.psp') }}</th>
                                <th>{{ $t('settlement.psp_cut_off.psp_cut_off') }}</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="row in rows" :key="row.psp">
                                <td class="align-middle font-weight-bold">{{ pspTitle(row.psp) }}</td>
                                <td>
                                    <datePicker
                                            type="time"
                                            format="HH:mm"
                                            display-format="HH:mm"
                                            :input-class="fieldError(row.psp, 'psp_cut_off') ? 'form-control is-invalid' : 'form-control'"
                                            :id="`${row.psp}_psp_cut_off`"
                                            :disabled="!canManage"
                                            v-model="row.psp_cut_off"/>
                                    <div class="invalid-feedback d-block">{{ fieldError(row.psp, 'psp_cut_off') }}</div>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <kt-form-action v-if="canManage" class="mt-2">
                        <button class="btn btn-md btn-success btn-wide" type="submit" ref="submit_btn">
                            {{ $t('settlement.psp_cut_off.save') }}
                        </button>
                    </kt-form-action>
                </form>
            </kt-panel-body>
        </kt-panel>
    </main-content>
</template>

<script>
    import mainContent from "@/components/main-content.vue";
    import {KTApp} from '@/plugins/core/app'
    import {settlementService} from "@/common/api.service";
    import toastr from "toastr";

    export default {
        name: "SettlementSettings",
        components: {
            'main-content': mainContent,
        },
        data() {
            return {
                loading: true,
                rows: [],
                errors: {},
            }
        },
        computed: {
            canManage() {
                return this.$can('manage-psp-cut-off', 'settlement')
            },
            toastr() {
                toastr.options = {
                    closeButton: true,
                    positionClass: "toast-bottom-center",
                    preventDuplicates: false,
                    rtl: false
                }
                return toastr
            },
        },
        mounted() {
            this.fetch()
        },
        methods: {
            fetch() {
                this.loading = true
                settlementService.fetchPspCutOffs()
                    .then(response => {
                        this.fill(response.data.data)
                    })
                    .catch(({response}) => {
                        this.toastr.error(response.data.message)
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },
            fill(items) {
                this.errors = {}
                this.rows = items.map(item => ({
                    psp: item.psp,
                    psp_cut_off: item.psp_cut_off || '',
                }))
            },
            save() {
                // Only PSPs with a value are sent; empty rows stay unconfigured.
                const psps = this.rows
                    .filter(row => !this.isBlank(row.psp_cut_off))
                    .map(row => ({psp: row.psp, psp_cut_off: row.psp_cut_off}))

                if (psps.length === 0) {
                    this.toastr.error(this.$t('settlement.psp_cut_off.errors.nothing_to_save'))
                    return
                }

                this.errors = {}
                const btn = this.$refs.submit_btn
                KTApp.progress(btn)
                settlementService.updatePspCutOffs({psps})
                    .then(response => {
                        this.fill(response.data.data)
                        this.toastr.success(response.data.message)
                    })
                    .catch(({response}) => {
                        this.setServerErrors(response.data.data, psps)
                        this.toastr.error(response.data.message)
                    })
                    .finally(() => {
                        KTApp.unprogress(btn)
                    })
            },
            // Server errors are keyed as "psps.{index}.{field}" relative to the sent list.
            setServerErrors(data, psps) {
                const errors = {}
                Object.keys(data || {}).forEach(key => {
                    const match = key.match(/^psps\.(\d+)\.(\w+)$/)
                    if (!match || !psps[match[1]]) {
                        return
                    }
                    const messages = data[key]
                    const psp = psps[match[1]].psp
                    errors[psp] = {...(errors[psp] || {}), [match[2]]: Array.isArray(messages) ? messages[0] : messages}
                })
                this.errors = errors
            },
            fieldError(psp, field) {
                return this.errors[psp] ? this.errors[psp][field] : null
            },
            isBlank(value) {
                return value === null || value === undefined || value === ''
            },
            pspTitle(psp) {
                return this.$t('psp.' + psp.toLowerCase())
            },
        }
    }
</script>

<style scoped>
    .info-tip {
        position: relative;
        display: inline-block;
        margin-right: 6px;
        color: #5d78ff;
        font-size: 1.3rem;
        vertical-align: middle;
        cursor: help;
        outline: none;
    }

    .info-tip-text {
        display: none;
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        z-index: 20;
        width: 340px;
        max-width: calc(100vw - 32px);
        padding: 10px 12px;
        border-radius: 4px;
        background: #2c2e3e;
        color: #fff;
        font-size: 12px;
        font-weight: 400;
        line-height: 1.8;
        text-align: right;
        white-space: normal;
        box-shadow: 0 2px 8px rgba(0, 0, 0, .15);
    }

    .info-tip:hover .info-tip-text,
    .info-tip:focus .info-tip-text {
        display: block;
    }

    .psp-cut-off-table th,
    .psp-cut-off-table td {
        text-align: center;
        white-space: nowrap;
    }

    .psp-cut-off-table input {
        min-width: 120px;
    }
</style>
